/**
 * Practice — pick a test, optionally narrow to topics, choose a length, and drill
 * with instant feedback (QuizRunner). Untimed; not graded server-side (that's the
 * exam). Ports renderPractice. CONFIG practice = "1b" + toolbar topic filter.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { questions, tests, TEST_KEYS, type TestKey, type TrainingQuestion } from '@/content/training';
import { trainingPath } from '../trainingNav';
import { PageHeader, Card, Button } from './TrainingUI';
import { QuizRunner } from './QuizRunner';

function shuffle<T>(a: T[]): T[] {
  const out = a.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const COUNTS = [10, 15, 25, 50];

function topicsFor(test: TestKey): string[] {
  const seen: string[] = [];
  for (const q of questions) {
    if (q.test === test && !seen.includes(q.topic)) seen.push(q.topic);
  }
  return seen;
}

function Pill({
  on,
  children,
  onClick,
}: {
  on: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button onClick={onClick} className={`pill${on ? ' on' : ''}`}>
      {children}
    </button>
  );
}

export function Practice() {
  const navigate = useNavigate();
  const [test, setTest] = useState<TestKey>('gk');
  const [topics, setTopics] = useState<string[]>([]);
  const [count, setCount] = useState(15);
  const [runId, setRunId] = useState(0);
  const [running, setRunning] = useState<TrainingQuestion[] | null>(null);

  const topicOptions = useMemo(() => topicsFor(test), [test]);

  const build = () => {
    const pool = questions.filter(
      (q) => q.test === test && (topics.length === 0 || topics.includes(q.topic)),
    );
    setRunning(shuffle(pool).slice(0, count));
    setRunId((n) => n + 1);
  };

  if (running) {
    return (
      <div className="mx-auto max-w-2xl">
        <QuizRunner
          key={runId}
          questions={running}
          onAgain={() => setRunning(null)}
          onExit={() => navigate(trainingPath('home'))}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title="Practice"
        sub="Untimed practice with instant feedback and an explanation for every answer. Pick a test, narrow to topics if you like, and go."
      />
      <Card className="space-y-5">
        <div>
          <h3 className="">1 · Which test?</h3>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {TEST_KEYS.map((k) => (
              <Pill
                key={k}
                on={test === k}
                onClick={() => {
                  setTest(k);
                  setTopics([]);
                }}
              >
                {tests[k].name}
              </Pill>
            ))}
          </div>
        </div>

        <div>
          <h3 className="">
            2 · Topics <span className="muted">(optional — all if none selected)</span>
          </h3>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {topicOptions.map((t) => (
              <Pill
                key={t}
                on={topics.includes(t)}
                onClick={() =>
                  setTopics((cur) =>
                    cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t],
                  )
                }
              >
                {t}
              </Pill>
            ))}
          </div>
        </div>

        <div>
          <h3 className="">3 · How many questions?</h3>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {COUNTS.map((n) => (
              <Pill key={n} on={count === n} onClick={() => setCount(n)}>
                {n}
              </Pill>
            ))}
          </div>
        </div>

        <Button variant="primary" onClick={build}>
          Start practice
        </Button>
      </Card>
    </div>
  );
}
